import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const RuntimeOperationContext = createContext(null);

const toScopeKey = (scope) => String(scope || 'global');
const toActionKey = (key) => String(key || '').trim();

export const RuntimeOperationProvider = ({ children }) => {
  const [lockedKeys, setLockedKeys] = useState(() => new Set());
  const queueByScopeRef = useRef(new Map());

  const isLocked = useCallback((actionKey) => {
    const normalized = toActionKey(actionKey);
    if (!normalized) {
      return false;
    }
    return lockedKeys.has(normalized);
  }, [lockedKeys]);

  const lock = useCallback((actionKey) => {
    const normalized = toActionKey(actionKey);
    if (!normalized) {
      return;
    }
    setLockedKeys((previous) => {
      const next = new Set(previous);
      next.add(normalized);
      return next;
    });
  }, []);

  const unlock = useCallback((actionKey) => {
    const normalized = toActionKey(actionKey);
    if (!normalized) {
      return;
    }
    setLockedKeys((previous) => {
      if (!previous.has(normalized)) {
        return previous;
      }
      const next = new Set(previous);
      next.delete(normalized);
      return next;
    });
  }, []);

  const withActionLock = useCallback(async (actionKey, handler) => {
    const normalized = toActionKey(actionKey);
    if (!normalized || typeof handler !== 'function') {
      throw new Error('withActionLock requer actionKey e handler validos.');
    }

    if (lockedKeys.has(normalized)) {
      return { skipped: true };
    }

    lock(normalized);
    try {
      const result = await handler();
      return { skipped: false, result };
    } finally {
      unlock(normalized);
    }
  }, [lockedKeys, lock, unlock]);

  const runSerialized = useCallback(async (scope, handler) => {
    if (typeof handler !== 'function') {
      throw new Error('runSerialized requer handler valido.');
    }

    const scopeKey = toScopeKey(scope);
    const previous = queueByScopeRef.current.get(scopeKey) || Promise.resolve();
    let unlockQueue;

    const next = new Promise((resolve) => {
      unlockQueue = resolve;
    });

    queueByScopeRef.current.set(scopeKey, previous.then(() => next));

    try {
      await previous;
      return await handler();
    } finally {
      unlockQueue();
      if (queueByScopeRef.current.get(scopeKey) === next) {
        queueByScopeRef.current.delete(scopeKey);
      }
    }
  }, []);

  const value = useMemo(() => ({
    isLocked,
    lock,
    unlock,
    withActionLock,
    runSerialized,
  }), [isLocked, lock, unlock, withActionLock, runSerialized]);

  return (
    <RuntimeOperationContext.Provider value={value}>
      {children}
    </RuntimeOperationContext.Provider>
  );
};

export const useRuntimeActionLock = () => {
  const context = useContext(RuntimeOperationContext);
  if (!context) {
    throw new Error('useRuntimeActionLock deve ser usado dentro de RuntimeOperationProvider.');
  }
  return context;
};

export default RuntimeOperationProvider;